import BaseMap from 'components/BaseMap/BaseMap';
import PlacesSearch from 'components/Header/PlacesSearch';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import SearchBar from 'components/SearchBar/SearchBar';
import BaseTable from 'components/Tables/BaseTable';
import { usePerms } from 'contexts/PermsContext';
import usePlaces from 'features/UserMenu/features/Places/hooks/usePlaces';
import useLocation from 'hooks/useLocation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import {
  FaBuilding,
  FaBuildingColumns,
  FaCity,
  FaFloppyDisk,
  FaHouse,
  FaMapLocation,
  FaPen,
  FaXmark,
} from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { selectUserSelectedLocationPlaceId } from 'store/selectors/User/userSelectors';
import UserMenuPanel from './components/UserMenuPanel';

export const UserPlaces = () => {
  const {
    places,
    loadingPlaces,
    fetchingPlaces,
    addingPlaces,
    selectingPlace,
    fullSelectedPlaceObject,
    selectPlace,
    addPlace,
    removePlace,
    renamePlace,
    changeHomePlace,
  } = usePlaces();
  const userSelectedPlaceId = useSelector(selectUserSelectedLocationPlaceId);
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const [mapCenter, setMapCenter] = useState([...effectiveLocation.coords]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [workingTitle, setWorkingTitle] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState(null);
  const perms = usePerms();
  const [params] = useSearchParams();

  function typeMap(type) {
    switch (type) {
      case 'locality':
        return 'City';
      case 'administrative_area_level_1':
        return 'State';
      case 'administrative_area_level_2':
        return 'County';
      case 'premise':
        return 'Building';
      case 'country':
        return 'Country';
      case 'geocode':
        return 'Location';
      default:
        return 'Building';
    }
  }

  function typeIcon(type) {
    switch (type) {
      case 'DefaultLocationUserHome':
        return <FaHouse size={20} color="currentColor" />;
      case 'locality':
        return <FaCity size={20} color="currentColor" />;
      case 'administrative_area_level_1':
        return <FaBuildingColumns size={20} color="currentColor" />;
      case 'administrative_area_level_2':
        return <FaBuildingColumns size={20} color="currentColor" />;
      case 'premise':
        return <FaBuilding size={20} color="currentColor" />;
      case 'geocode':
        return <FaMapLocation size={20} color="currentColor" />;
      default:
        return <FaBuilding size={20} color="currentColor" />;
    }
  }

  useEffect(() => {
    if (loadingPlaces || fetchingPlaces) return;

    if (places.length === 0) {
      setSelectedPlace(null);
      return;
    }

    setSelectedPlace(fullSelectedPlaceObject || places[0]);
  }, [loadingPlaces, fetchingPlaces, places, fullSelectedPlaceObject]);

  const addPreviewLocation = (location) => {
    setMapCenter([location.latitude, location.longitude]);
    setSelectedLocation(location);
  };

  const saveLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      const savedPlace = places.find((place) => place.place_id === location.place_id);

      if (savedPlace) {
        await selectPlace(savedPlace);
        setSelectedPlace(savedPlace);
        return;
      }

      await addPlace(location);
    },
    [addPlace, places, selectPlace],
  );

  const editLocationTitle = useCallback(
    async (location, title) => {
      if (!location?.place_id) return;

      await renamePlace(location.place_id, title);
    },
    [renamePlace],
  );

  const changeHomeLocation = useCallback(
    async (location) => {
      const newHomePlace = await changeHomePlace(location);

      setEditIndex(-1);

      if (newHomePlace) setSelectedPlace(newHomePlace);
    },
    [changeHomePlace],
  );

  useEffect(() => {
    if (selectedPlace) {
      setMapCenter([selectedPlace.latitude, selectedPlace.longitude]);
      setSelectedLocation(selectedPlace);
    }
  }, [selectedPlace]);

  const removeLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      const newPlaces = await removePlace(location.place_id);

      const nextSelectedPlace =
        newPlaces?.find((place) => place.place_id === userSelectedPlaceId) || newPlaces[0] || null;
      setSelectedPlace(nextSelectedPlace);

      // This ensures that if the user deletes the currently selected place, we select the next best place
      if (nextSelectedPlace?.place_id !== userSelectedPlaceId) {
        await selectPlace(nextSelectedPlace);
      }
    },
    [removePlace, userSelectedPlaceId, selectPlace],
  );

  const searchFilter = useCallback(
    (place) => {
      if (!place) return false;

      const search = params.get('search');

      if (search) {
        return (
          place.title
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, '')) ||
          place.state
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, ''))
        );
      } else return true;
    },
    [params],
  );

  const columns = useMemo(() => {
    return [
      {
        header: 'Title',
        accessorKey: 'title',
        Cell: ({ row, cell }) => {
          const title = cell.getValue();
          const typeIcon = row.original.typeIcon;
          const index = row.original.index;
          const place = row.original.place;

          return (
            <div className="d-flex align-items-center gap-2">
              <span className="user-menu-place-type-icon" aria-hidden="true">
                {typeIcon}
              </span>
              {editIndex !== index ? (
                <p className="mb-0">{title}</p>
              ) : (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    editLocationTitle(place, workingTitle);
                    setEditIndex(-1);
                  }}
                >
                  <Form.Control
                    aria-label="Place title"
                    value={workingTitle}
                    onChange={(e) => {
                      setWorkingTitle(e.target.value);
                    }}
                    className="w-100 rounded-2"
                  />
                </Form>
              )}
            </div>
          );
        },
        size: 180,
      },
      {
        header: 'Type',
        accessorKey: 'type',
        Cell: ({ row, cell }) => {
          const type = cell.getValue();
          return <p className="mb-0">{type}</p>;
        },
        size: 100,
      },
      {
        header: 'Location',
        accessorKey: 'location',
        Cell: ({ row, cell }) => {
          const location = cell.getValue();
          return <p className="mb-0">{location}</p>;
        },
        size: 130,
      },
      {
        header: '',
        id: 'Actions',
        columnDefType: 'display',
        Cell: ({ row, cell }) => {
          const place = row.original.place;
          const index = row.original.index;

          return (
            <div className="d-flex justify-content-end align-items-center gap-1">
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                editIndex === index && (
                  <button
                    type="button"
                    className="user-menu-icon-button"
                    aria-label={`Save the title of ${place.title || 'this place'}`}
                    title="Save title"
                    onClick={() => {
                      editLocationTitle(place, workingTitle);
                      setEditIndex(-1);
                    }}
                  >
                    <FaFloppyDisk aria-hidden="true" />
                  </button>
                )}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                editIndex !== index && (
                  <button
                    type="button"
                    className="user-menu-icon-button"
                    aria-label={`Rename ${place.title || 'this place'}`}
                    title="Rename"
                    onClick={() => {
                      setEditIndex(index);
                      setWorkingTitle(place.title || '');
                    }}
                  >
                    <FaPen size={14} aria-hidden="true" />
                  </button>
                )}
              {!place.DefaultLocationUserHome &&
                perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable']) && (
                  <button
                    type="button"
                    className="user-menu-icon-button"
                    aria-label={`Make ${place.title || 'this place'} your home place`}
                    title="Make home"
                    onClick={(e) => {
                      e.stopPropagation();
                      changeHomeLocation(place);
                    }}
                  >
                    <FaHouse size={14} aria-hidden="true" />
                  </button>
                )}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                !place.DefaultLocationUserHome && (
                  <button
                    type="button"
                    className="user-menu-icon-button"
                    data-tone="danger"
                    aria-label={`Delete ${place.title || 'this place'}`}
                    title="Delete"
                    onClick={() => {
                      setPlaceToDelete(place);
                      setShowConfirmDelete(true);
                    }}
                  >
                    <FaXmark size={15} aria-hidden="true" />
                  </button>
                )}
            </div>
          );
        },
        enableSorting: false,
        size: 180,
      },
    ];
  }, [changeHomeLocation, editIndex, perms, workingTitle, editLocationTitle]);

  const rows = useMemo(() => {
    return places
      .map((place, index) => ({
        id: place.place_id,
        title: place.title,
        typeIcon: typeIcon(place.type),
        type: typeMap(place.type),
        location: place.place_data?.formatted_address,
        place: place,
        index: index,
      }))
      .filter(searchFilter);
  }, [places, searchFilter]);

  return (
    <Container fluid className="page-scroll-area user-menu-page px-0">
      {showConfirmDelete && (
        <ConfirmationModal
          show={showConfirmDelete}
          handleShow={() => setShowConfirmDelete(false)}
          bodyTxt={`Are you sure you want to delete this place?`}
          cancelBtnTxt="No"
          confirmBtnTxt="Yes"
          confirmHandler={() => {
            removeLocation(placeToDelete);
            setShowConfirmDelete(false);
          }}
        />
      )}
      <div className="user-menu-grid" data-layout="halves-wide">
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) && (
          <UserMenuPanel icon={<FaMapLocation />} title="Add a Place">
            <div>
              <PlacesSearch
                noBack
                noAutoAdd
                places={places}
                setSearching={() => {}}
                addLocation={addPreviewLocation}
                setMapCenter={setMapCenter}
                quickSave
                cwValidated
              />
            </div>
            {/* Map Container - height is 75% of the width */}
            <div className="ratio ratio-4x3 w-100 user-menu-place-map">
              <BaseMap mapCenter={mapCenter} zoom={11} disableMapReset></BaseMap>
            </div>
            <div className="user-menu-actions">
              <p className="mb-0">{selectedLocation?.place_data?.formatted_address}</p>
              <Button
                onClick={() => {
                  saveLocation(selectedLocation);
                }}
                disabled={!selectedLocation || addingPlaces || selectingPlace}
                variant="primary"
              >
                Save
              </Button>
            </div>
          </UserMenuPanel>
        )}
        <UserMenuPanel
          icon={<FaHouse />}
          title="Saved Places"
          actions={<SearchBar className="p-2 rounded-4 w-auto border-0"></SearchBar>}
        >
          <BaseTable
            columns={columns}
            rows={rows}
            selectedRowId={selectedPlace?.place_id}
            customStyles={{ cell: { borderBottom: '1px solid var(--color-divider)' } }}
            onRowClick={(row) => {
              setSelectedPlace(row.place);
            }}
          />
        </UserMenuPanel>
      </div>
    </Container>
  );
};
